/**
 * ProfileSetupPage — Phase 1 core flow
 *
 * Step 1: Upload PDF CV → backend extracts text + calls OpenRouter → returns parsed profile
 * Step 2: User reviews / edits parsed fields in a form
 * Step 3: User confirms → POST /api/cv-profile/ saves to DB
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE as API, getToken } from '../config';
import { useAuth } from '../hooks/useAuth';

// ── Types matching the backend CVProfileCreate schema ──────────────────────

interface WorkExperience {
  title: string;
  company: string;
  location: string;
  period: string;
  achievements: string[];
  technologies: string[];
}

interface Education {
  degree: string;
  institution: string;
  location: string;
  year: string;
  details: string;
}

interface Skills {
  technical: string[];
  languages: string[];
  tools: string[];
  soft_skills: string[];
}

interface ParsedProfile {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  citizenship?: string;
  linkedin_url: string;
  github_url: string;
  portfolio_url: string;
  professional_summary: string;
  current_title: string;
  target_titles: string[];
  industry: string;
  total_years_experience: number;
  work_experience: WorkExperience[];
  education: Education[];
  skills: Skills;
  projects: any[];
  certifications: any[];
}

// ── Upload step ─────────────────────────────────────────────────────────────

function UploadStep({ onParsed }: { onParsed: (p: ParsedProfile, w: string[]) => void }) {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted.');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const form = new FormData();
      form.append('file', file);

      const res = await fetch(`${API}/api/cv-profile/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }

      const data = await res.json();
      onParsed(data.parsed, data.warnings ?? []);
      toast.success('CV parsed — review the result below.');
    } catch (e: any) {
      toast.error(e.message ?? 'Upload failed.');
    } finally {
      setLoading(false);
    }
  }, [onParsed]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  }, [upload]);

  return (
    <div className="max-w-xl mx-auto">
      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors
          ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); }}
        />
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Extracting and parsing your CV…</p>
            <p className="text-sm text-gray-400 mt-1">This takes 10–30 seconds</p>
          </>
        ) : (
          <>
            <div className="text-5xl mb-4">📄</div>
            <p className="text-lg font-medium text-gray-700">Drop your CV PDF here</p>
            <p className="text-sm text-gray-500 mt-1">or click to browse</p>
            <p className="text-xs text-gray-400 mt-3">Text-based PDF only · max 10 MB</p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function Field({
  label, value, onChange, multiline = false, type = 'text'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  type?: string;
}) {
  const base = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {multiline ? (
        <textarea className={`${base} min-h-[80px]`} value={value} onChange={e => onChange(e.target.value)} />
      ) : (
        <input className={base} type={type} value={value} onChange={e => onChange(e.target.value)} />
      )}
    </div>
  );
}

function TagsField({ label, tags, onChange }: { label: string; tags: string[]; onChange: (t: string[]) => void }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput('');
  };
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1 mb-1">
        {tags.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
            {t}
            <button onClick={() => onChange(tags.filter((_, j) => j !== i))} className="hover:text-red-500">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Type and press Enter"
        />
        <button onClick={add} className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">Add</button>
      </div>
    </div>
  );
}

// ── Review / edit step ───────────────────────────────────────────────────────

function ReviewStep({
  profile,
  warnings,
  onSave,
  onBack,
}: {
  profile: ParsedProfile;
  warnings: string[];
  onSave: (p: ParsedProfile) => Promise<void>;
  onBack: () => void;
}) {
  const [form, setForm] = useState<ParsedProfile>(profile);
  const [saving, setSaving] = useState(false);

  const set = (field: keyof ParsedProfile, value: any) =>
    setForm(f => ({ ...f, [field]: value }));

  const setSkills = (key: keyof Skills, value: string[]) =>
    setForm(f => ({ ...f, skills: { ...f.skills, [key]: value } }));

  const handleSave = async () => {
    if (!form.full_name.trim()) { toast.error('Full name is required.'); return; }
    if (!form.professional_summary.trim()) { toast.error('Professional summary is required.'); return; }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-1">
          <p className="text-sm font-medium text-amber-800">Review needed:</p>
          {warnings.map((w, i) => <p key={i} className="text-sm text-amber-700">• {w}</p>)}
        </div>
      )}

      {/* Personal info */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name *" value={form.full_name} onChange={v => set('full_name', v)} />
          <Field label="Email *" value={form.email} onChange={v => set('email', v)} type="email" />
          <Field label="Phone" value={form.phone ?? ''} onChange={v => set('phone', v)} />
          <Field label="Location (City, Country)" value={form.location ?? ''} onChange={v => set('location', v)} />
          <Field label="Citizenship / work permits (e.g. Spain — EU)" value={form.citizenship ?? ''} onChange={v => set('citizenship', v)} />
          <Field label="LinkedIn URL" value={form.linkedin_url ?? ''} onChange={v => set('linkedin_url', v)} />
          <Field label="GitHub URL" value={form.github_url ?? ''} onChange={v => set('github_url', v)} />
          <Field label="Portfolio URL" value={form.portfolio_url ?? ''} onChange={v => set('portfolio_url', v)} />
          <Field label="Current title" value={form.current_title ?? ''} onChange={v => set('current_title', v)} />
          <Field label="Industry" value={form.industry ?? ''} onChange={v => set('industry', v)} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Years of experience</label>
            <input
              type="number" min={0} max={60}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.total_years_experience}
              onChange={e => set('total_years_experience', parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
        <div className="mt-4">
          <Field
            label="Professional summary *"
            value={form.professional_summary}
            onChange={v => set('professional_summary', v)}
            multiline
          />
        </div>
        <div className="mt-4">
          <TagsField
            label="Target job titles"
            tags={form.target_titles ?? []}
            onChange={v => set('target_titles', v)}
          />
        </div>
      </section>

      {/* Skills */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Skills</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <TagsField label="Technical skills" tags={form.skills?.technical ?? []} onChange={v => setSkills('technical', v)} />
          <TagsField label="Tools & platforms" tags={form.skills?.tools ?? []} onChange={v => setSkills('tools', v)} />
          <TagsField label="Languages" tags={form.skills?.languages ?? []} onChange={v => setSkills('languages', v)} />
          <TagsField label="Soft skills" tags={form.skills?.soft_skills ?? []} onChange={v => setSkills('soft_skills', v)} />
        </div>
      </section>

      {/* Work experience (read-only preview, editable in Phase 1b) */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Work Experience</h2>
        <p className="text-xs text-gray-500 mb-4">Your starting point — full experience editor coming in a next update.</p>
        {(form.work_experience ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 italic">No work experience extracted. You can add it after saving.</p>
        ) : (
          <div className="space-y-4">
            {form.work_experience.map((exp, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{exp.title}</p>
                    <p className="text-sm text-gray-600">{exp.company} · {exp.period}</p>
                    {exp.location && <p className="text-xs text-gray-400">{exp.location}</p>}
                  </div>
                </div>
                {exp.achievements?.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {exp.achievements.slice(0, 3).map((a, j) => (
                      <li key={j} className="text-xs text-gray-600 flex gap-1.5">
                        <span className="text-gray-400 mt-0.5">•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                    {exp.achievements.length > 3 && (
                      <li className="text-xs text-gray-400">+{exp.achievements.length - 3} more</li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Education */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Education</h2>
        {(form.education ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 italic">No education extracted.</p>
        ) : (
          <div className="space-y-3">
            {form.education.map((edu, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                <p className="font-medium text-gray-900">{edu.degree}</p>
                <p className="text-sm text-gray-600">{edu.institution} · {edu.year}</p>
                {edu.details && <p className="text-xs text-gray-500 mt-1">{edu.details}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Actions */}
      <div className="flex items-center justify-between pb-8">
        <button
          onClick={onBack}
          className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          ← Upload a different file
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save profile →'}
        </button>
      </div>
    </div>
  );
}

// ── Page orchestrator ────────────────────────────────────────────────────────

// ── Answer-questions step (AI drafts a starter profile, no PDF) ──────────────
function AnswersStep({ onParsed }: { onParsed: (p: ParsedProfile, w: string[]) => void }) {
  const { user } = useAuth();
  const [freeText, setFreeText] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [a, setA] = useState({
    // Pre-fill what we already know from the account — fewer empty boxes.
    full_name: user?.full_name || '', current_title: '', years_experience: '',
    top_skills: '', target_title: '', industry: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: string) => setA(prev => ({ ...prev, [k]: v }));

  const draft = async (payload: Record<string, any>) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cv-profile/draft`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not draft your profile.');
      onParsed(data.parsed, [
        'This is an AI draft. Add your real employers, dates and achievements before saving — the truth-check depends on accurate facts.',
      ]);
    } catch (err: any) {
      toast.error(err.message || 'Draft failed');
    } finally {
      setLoading(false);
    }
  };

  // One-phrase mode: everything from a single sentence.
  const submitPhrase = (e: React.FormEvent) => {
    e.preventDefault();
    if (!freeText.trim()) {
      toast.error('Write one sentence about yourself first.');
      return;
    }
    draft({ free_text: freeText.trim(), full_name: a.full_name || undefined });
  };

  // Detailed mode: the structured questions.
  const submitDetails = (e: React.FormEvent) => {
    e.preventDefault();
    if (!a.current_title && !a.top_skills) {
      toast.error('Tell us at least your role or your top skills.');
      return;
    }
    draft({ ...a, years_experience: a.years_experience || undefined });
  };

  const inputCls = 'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  return (
    <div className="max-w-xl mx-auto space-y-4">
      {/* One-phrase mode — the fast path */}
      <form onSubmit={submitPhrase} className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
        <label className="block text-sm font-semibold text-gray-800">
          ✨ Describe yourself in one sentence
        </label>
        <textarea
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          rows={3}
          value={freeText}
          onChange={e => setFreeText(e.target.value)}
          placeholder={'e.g. "DevOps engineer, 8 years with Kubernetes, Terraform and AWS, looking for a senior platform role in SaaS"'}
          autoFocus
        />
        <button type="submit" disabled={loading}
          className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Drafting your profile…' : 'Autofill my profile →'}
        </button>
        <p className="text-xs text-gray-400 text-center">That's it — we extract your role, experience and skills from this one phrase.</p>
      </form>

      {/* Detailed mode — optional */}
      <div className="text-center">
        <button type="button" onClick={() => setShowDetails(s => !s)} className="text-sm text-gray-500 hover:text-gray-700">
          {showDetails ? 'Hide detailed questions ▲' : 'Prefer detailed questions? ▼'}
        </button>
      </div>

      {showDetails && (
        <form onSubmit={submitDetails} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-semibold text-gray-600 mb-1">Full name</label>
              <input className={inputCls} value={a.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Marie Hughes" /></div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1">Current / most recent title</label>
              <input className={inputCls} value={a.current_title} onChange={e => set('current_title', e.target.value)} placeholder="DevOps Engineer" /></div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1">Years of experience</label>
              <input className={inputCls} type="number" min="0" value={a.years_experience} onChange={e => set('years_experience', e.target.value)} placeholder="8" /></div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1">Target job title</label>
              <input className={inputCls} value={a.target_title} onChange={e => set('target_title', e.target.value)} placeholder="Senior Platform Engineer" /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-semibold text-gray-600 mb-1">Top skills (comma separated)</label>
              <input className={inputCls} value={a.top_skills} onChange={e => set('top_skills', e.target.value)} placeholder="Kubernetes, Terraform, AWS, CI/CD" /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-semibold text-gray-600 mb-1">Industry (optional)</label>
              <input className={inputCls} value={a.industry} onChange={e => set('industry', e.target.value)} placeholder="Tech / SaaS" /></div>
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Drafting your profile…' : 'Draft my profile with AI →'}
          </button>
        </form>
      )}
    </div>
  );
}

type Step = 'choose' | 'upload' | 'answers' | 'review' | 'done';

const ProfileSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choose');
  const [parsed, setParsed] = useState<ParsedProfile | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saved, setSaved] = useState<ParsedProfile | null>(null);

  // If a profile already exists, offer direct editing — no need to re-upload.
  React.useEffect(() => {
    fetch(`${API}/api/cv-profile/`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(p => setSaved(p || null))
      .catch(() => {});
  }, []);

  const editSaved = () => {
    if (!saved) return;
    setParsed(saved);
    setWarnings([]);
    setStep('review');
  };

  const onParsed = (p: ParsedProfile, w: string[]) => {
    setParsed(p);
    setWarnings(w);
    setStep('review');
  };

  const onSave = async (profile: ParsedProfile) => {
    const token = getToken();

    // Check if profile exists already — PUT vs POST
    const existsRes = await fetch(`${API}/api/cv-profile/exists`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { exists } = await existsRes.json();

    const method = exists ? 'PUT' : 'POST';
    const res = await fetch(`${API}/api/cv-profile/`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...profile,
        preferred_template: 'modern',
        preferred_language: 'en',
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to save profile.');
    }

    toast.success('Profile saved! You can now generate tailored CVs.');
    setStep('done');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      {/* Header — 3 stages: Start · Review · Done */}
      {(() => {
        const stageOf = step === 'choose' || step === 'upload' || step === 'answers' ? 0 : step === 'review' ? 1 : 2;
        const labels = ['Start', 'Review & Edit', 'Done'];
        return (
          <div className="max-w-3xl mx-auto mb-8">
            <div className="flex items-center gap-3 mb-2">
              {labels.map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                    ${stageOf === i ? 'bg-blue-600 text-white' : stageOf > i ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {stageOf > i ? '✓' : i + 1}
                  </div>
                  {i < 2 && <div className="flex-1 h-0.5 bg-gray-200" />}
                </React.Fragment>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              {labels.map((l, i) => <span key={l} className={i === 1 ? 'text-center' : i === 2 ? 'text-right' : ''}>{l}</span>)}
            </div>
          </div>
        );
      })()}

      {step === 'choose' && (
        <div>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{saved ? 'Your profile' : 'Set up your profile'}</h1>
            <p className="text-gray-500 mt-1">
              {saved ? 'Edit your saved profile, or rebuild it from scratch.' : 'Two ways to start — pick whichever is easier.'}
            </p>
          </div>
          {saved && (
            <div className="max-w-2xl mx-auto mb-4">
              <button onClick={editSaved}
                className="w-full text-left bg-white border-2 border-blue-200 rounded-xl p-6 hover:border-blue-400 hover:shadow-md transition">
                <div className="text-3xl mb-3">✏️</div>
                <h3 className="font-semibold text-gray-900">Edit my saved profile</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Change any field of your current profile ({saved.full_name || 'unnamed'}) — no re-upload needed.
                </p>
              </button>
            </div>
          )}
          <div className="max-w-2xl mx-auto grid sm:grid-cols-2 gap-4">
            <button onClick={() => setStep('upload')}
              className="text-left bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-400 hover:shadow-md transition">
              <div className="text-3xl mb-3">📄</div>
              <h3 className="font-semibold text-gray-900">Upload my CV</h3>
              <p className="text-sm text-gray-500 mt-1">Have a CV/résumé PDF? We'll read it and fill your profile automatically.</p>
            </button>
            <button onClick={() => setStep('answers')}
              className="text-left bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-400 hover:shadow-md transition">
              <div className="text-3xl mb-3">✨</div>
              <h3 className="font-semibold text-gray-900">Answer a few questions</h3>
              <p className="text-sm text-gray-500 mt-1">No CV handy? Answer a few quick questions and we'll draft a starting profile for you.</p>
            </button>
          </div>
        </div>
      )}

      {step === 'upload' && (
        <div>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Upload your CV</h1>
            <p className="text-gray-500 mt-1">Upload your CV PDF and we'll extract your information automatically.</p>
          </div>
          <UploadStep onParsed={onParsed} />
          <div className="text-center mt-4">
            <button onClick={() => setStep('choose')} className="text-sm text-gray-500 hover:text-gray-700">← Back</button>
          </div>
        </div>
      )}

      {step === 'answers' && (
        <div>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Tell us about yourself</h1>
            <p className="text-gray-500 mt-1">A few quick questions — we'll draft a profile you can refine.</p>
          </div>
          <AnswersStep onParsed={onParsed} />
          <div className="text-center mt-4">
            <button onClick={() => setStep('choose')} className="text-sm text-gray-500 hover:text-gray-700">← Back</button>
          </div>
        </div>
      )}

      {step === 'review' && parsed && (
        <div>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Review your profile</h1>
            <p className="text-gray-500 mt-1">Check the extracted information and fix anything that looks wrong.</p>
          </div>
          <ReviewStep
            profile={parsed}
            warnings={warnings}
            onSave={onSave}
            onBack={() => setStep('choose')}
          />
        </div>
      )}

      {step === 'done' && (
        <div className="max-w-md mx-auto text-center py-20">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile saved!</h1>
          <p className="text-gray-500 mb-8">
            You're ready to generate tailored CVs and cover letters.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/app/generate')}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
            >
              Generate a CV →
            </button>
            <button
              onClick={() => navigate('/app/jobs')}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Browse jobs
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSetupPage;
